<template>
	<div class="video-wrapper">
		<h2 class="section-title">视频转 Gif</h2>

    <!-- 加载提示 -->
    <loading-tip 
      :visible="isLoading" 
      :text="loadingText" 
      :subtext="loadingSubtext"
      :progress="loadingProgress"
    />

    <div
      v-if="!videoUrl"
      class="upload-area"
      :class="{ dragging: isDragging }"
      @click="triggerVideoUpload"
      @dragover.prevent
      @drop.prevent="handleDrop"
      @dragenter="dragEnter"
      @dragleave="dragLeave"
    >
      <div class="upload-inner">
        <div class="upload-icon">🎬</div>
        <div class="upload-text">点击上传视频</div>
        <div class="upload-hint">支持 MP4、MOV、AVI 等常见格式</div>
        <div class="size-hint">{{ isMobile ? '建议不超过 30MB' : '建议不超过 100MB' }}</div>
      </div>
    </div>

    <input
      ref="videoInput"
      type="file"
      accept="video/*"
      class="hidden-video-input"
      @change="uploaderVideo"
    >

		<div class="preview-container" v-show="videoUrl">
      <div class="preview-title">视频预览</div>
      <div class="video-info" v-if="videoInfo">
        <span class="info-item">原始尺寸: {{ videoInfo.width }}×{{ videoInfo.height }}</span>
        <span class="info-item">时长: {{ formatDuration(videoInfo.duration) }}</span>
        <span class="info-item">大小: {{ formatFileSizeDisplay(videoInfo.size) }}</span>
      </div>
		  <video
        ref="video"
        class="video"
        controls="true"
        :src="videoUrl"
        @play="play"
        @pause="pause"
        @loadedmetadata="onVideoLoaded"
      />
    </div>

		<div class="controls">
      <div class="control-group">
        <label class="control-label">输出尺寸</label>
        <div class="size-input-group">
          <div class="size-inputs">
            <label class="size-label">宽度:</label>
            <input type="number" class="input size-input" v-model="width" min="1" max="1920" @input="updateSize" :disabled="isConverting">
            <span class="size-separator">×</span>
            <label class="size-label">高度:</label>
            <input type="number" class="input size-input" v-model="height" min="1" max="1080" @input="updateSize" :disabled="isConverting">
            <span class="size-unit">px</span>
          </div>
        </div>
        <div class="size-presets">
          <button class="preset-btn" @click="setPresetSize(320, 240)" :disabled="isConverting">QVGA</button>
          <button class="preset-btn" @click="setPresetSize(640, 480)" :disabled="isConverting">VGA</button>
          <button class="preset-btn" @click="setPresetSize(800, 600)" :disabled="isConverting">SVGA</button>
          <button class="preset-btn" @click="setPresetSize(1024, 768)" :disabled="isConverting">XGA</button>
          <button class="preset-btn" @click="setPresetSize(1920, 1080)" :disabled="isConverting">FHD</button>
          <button class="preset-btn" @click="setPresetSize('auto')" :disabled="isConverting">原始比例</button>
        </div>
      </div>

      <div class="control-group">
        <label class="control-label">时间范围</label>
        <div class="time-range-container">
          <div class="time-input-group">
            <label class="time-label">开始:</label>
            <input type="number" class="input time-input" v-model.number="startTime" min="0" :max="endTime" step="0.1" @input="updateTimeRange" :disabled="isConverting">
            <span class="time-unit">秒</span>
          </div>
          <div class="time-input-group">
            <label class="time-label">结束:</label>
            <input type="number" class="input time-input" v-model.number="endTime" :min="startTime" :max="videoInfo ? videoInfo.duration : 0" step="0.1" @input="updateTimeRange" :disabled="isConverting">
            <span class="time-unit">秒</span>
          </div>
        </div>
        <div class="time-range-slider" v-if="videoInfo">
          <input
            type="range"
            class="time-slider"
            min="0"
            :max="videoInfo.duration"
            step="0.1"
            v-model.number="startTime"
            :disabled="isConverting"
          >
          <input
            type="range"
            class="time-slider"
            min="0"
            :max="videoInfo.duration"
            step="0.1"
            v-model.number="endTime"
            :disabled="isConverting"
          >
        </div>
      </div>

      <div class="control-group">
        <label class="control-label">
          FPS (帧率) <span class="value-display">{{ fps }}</span>
        </label>
        <input
          type="range"
          min="5"
          max="30"
          step="1"
          v-model.number="fps"
          class="range"
          :disabled="isConverting"
        >
        <div class="fps-presets">
          <button class="preset-btn" @click="setFps(10)" :disabled="isConverting">10fps</button>
          <button class="preset-btn" @click="setFps(15)" :disabled="isConverting">15fps</button>
          <button class="preset-btn" @click="setFps(24)" :disabled="isConverting">24fps</button>
          <button class="preset-btn" @click="setFps(30)" :disabled="isConverting">30fps</button>
        </div>
      </div>

      <div class="control-group">
        <label class="control-label">
          质量(1~30, 越低越清晰) <span class="value-display">{{ quality }}</span>
        </label>
        <input
          type="range"
          min="1"
          max="30"
          step="1"
          v-model.number="quality"
          class="range"
          :disabled="isConverting"
        >
      </div>

      <div class="control-group">
        <label class="control-label">颜色量化（减少闪烁）</label>
        <div class="quantize-controls">
          <label class="time-label">启用：</label>
          <input type="checkbox" v-model="useQuantization" :disabled="isConverting">
          <label class="time-label" style="margin-left:12px">色深：</label>
          <input type="range" min="1" max="8" step="1" v-model.number="colorBits" :disabled="isConverting" class="range">
          <span class="value-display">{{ colorBits }}bit</span>
        </div>
      </div>

      <div class="control-group">
        <label class="control-label">文字/水印覆盖</label>
        <div class="overlay-controls">
          <input type="text" class="input overlay-text-input" v-model="overlayText" placeholder="在GIF上显示的文字" :disabled="isConverting">
          <div style="margin-top:8px;display:flex;gap:12px;align-items:center;flex-wrap:wrap;">
            <div style="display:flex;align-items:center;gap:4px;">
              <label class="time-label">大小:</label>
              <input type="number" class="input size-input-small" v-model.number="overlayFontSize" min="8" max="72" :disabled="isConverting">
              <span class="input-unit">px</span>
            </div>
            <div style="display:flex;align-items:center;gap:4px;">
              <label class="time-label">颜色:</label>
              <input type="color" v-model="overlayColor" :disabled="isConverting" style="width:40px;height:32px;border:1px solid #cbd5e1;border-radius:4px;">
            </div>
            <div style="display:flex;align-items:center;gap:4px;">
              <label class="time-label">不透明度:</label>
              <input type="range" min="0.1" max="1" step="0.1" v-model.number="overlayOpacity" :disabled="isConverting" style="width:80px;">
              <span class="value-display">{{ Math.round(overlayOpacity * 100) }}%</span>
            </div>
            <div style="display:flex;align-items:center;gap:4px;">
              <label class="time-label">位置:</label>
              <select v-model="overlayPosition" :disabled="isConverting" style="padding:4px 8px;border:1px solid #cbd5e1;border-radius:4px;">
                <option value="bottom-right">右下</option>
                <option value="bottom-left">左下</option>
                <option value="top-right">右上</option>
                <option value="top-left">左上</option>
                <option value="center">居中</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div class="control-group" v-if="estimatedSize > 0">
        <label class="control-label">预计文件大小</label>
        <div class="estimated-size">{{ formatFileSizeDisplay(estimatedSize) }}</div>
        <div class="size-warning" v-if="estimatedSize > 5 * 1024 * 1024">
          <span class="warning-icon">⚠️</span>
          文件较大，可能影响加载速度
        </div>
      </div>
		</div>

    <p class="helper-text">
      提示：设置好参数后，点击"开始转换"按钮即可生成GIF，无需播放视频。转换完成后可以预览和下载。
    </p>

    <div class="progress-container" v-if="isConverting">
      <div class="progress-label">正在生成 GIF... {{ convertProgress }}%</div>
      <div class="progress-bar">
        <div class="progress-fill" :style="{ width: convertProgress + '%' }"></div>
      </div>
      <div class="progress-actions">
        <van-button type="danger" size="small" @click="cancelConversion">取消</van-button>
      </div>
    </div>

    <div class="actions">
      <div class="action-buttons">
        <van-button
          type="primary"
          :disabled="!videoUrl || isConverting"
          @click="startConversion"
          class="convert-btn"
        >
          {{ isConverting ? '正在转换...' : '开始转换' }}
        </van-button>
        <van-button
          type="info"
          :disabled="!gifImg || isConverting"
          @click="toDownloadVideo"
          class="download-btn"
        >
          下载 GIF
        </van-button>
      </div>
    </div>

		<img v-show="gifImg" :src="gifImg" alt="预览" ref="img" class="preview-img">
	</div>
</template>

<script>
import GIF from "gif.js";
import {Toast} from "vant";
import LoadingTip from './LoadingTip.vue'
import { isMobile, getDeviceConfig, checkFileSize, formatFileSize, getScaledSize, yieldToMain, downloadFile } from './utils.js'

export default {
  components: {
    LoadingTip
  },
  data () {
    const config = getDeviceConfig()
    return {
      videoGif: '',
      quality: config.defaultQuality,
      delay: Math.round(1000 / config.defaultFps),
      fps: config.defaultFps,
      width: 0,
      height: 0,
      startTime: 0,
      endTime: 0,
      videoUrl: '',
      videoInfo: null,
      gifImg: '',
      isDragging: false,
      estimatedSize: 0,
      isConverting: false,
      convertProgress: 0,
      frameCount: 0,
      totalFrames: 0,
      // overlay and quantization settings
      overlayText: '',
      overlayFontSize: 16,
      overlayColor: '#ffffff',
      overlayOpacity: 0.9,
      overlayPosition: 'bottom-right',
      useQuantization: true,
      colorBits: 5,
      
      // 加载状态
      isLoading: false,
      loadingText: '',
      loadingSubtext: '',
      loadingProgress: -1,
      
      // 设备配置
      isMobile: config.isMobile,
      maxVideoSize: config.maxVideoSize,
      maxProcessWidth: config.maxProcessWidth,
      maxProcessHeight: config.maxProcessHeight,
      batchSize: config.batchSize
		}
	},
  beforeDestroy() {
    // 清理资源
    if (this.gifImg) {
      URL.revokeObjectURL(this.gifImg);
      this.gifImg = '';
    }
    if (this.videoUrl) {
      URL.revokeObjectURL(this.videoUrl);
      this.videoUrl = '';
    }
    if (this._gifStallChecker) {
      clearInterval(this._gifStallChecker);
      this._gifStallChecker = null;
    }
  },
  watch: {
    fps: function(newFps) {
      this.delay = Math.round(1000 / newFps);
      this.calculateEstimatedSize();
    },
    quality: function() {
      this.calculateEstimatedSize();
    },
    startTime: function() {
      this.calculateEstimatedSize();
    },
    endTime: function() {
      this.calculateEstimatedSize();
    }
  },
	methods: {
    triggerVideoUpload () {
      if (this.$refs.videoInput) {
        this.$refs.videoInput.click()
      }
    },
    onVideoLoaded () {
      const video = this.$refs.video;
      if (video && this.videoUrl) {
        this.videoInfo = {
          width: video.videoWidth,
          height: video.videoHeight,
          duration: video.duration,
          size: this.getVideoFileSize()
        };
        
        // 设置默认的时间范围
        this.endTime = video.duration;
        
        // 移动端自动压缩尺寸
        if (this.isMobile) {
          const scaled = getScaledSize(
            video.videoWidth, 
            video.videoHeight, 
            this.maxProcessWidth, 
            this.maxProcessHeight
          )
          this.width = scaled.width
          this.height = scaled.height
        } else {
          // 设置默认尺寸
          if (!this.width || !this.height) {
            const scale = 3;
            this.width = Math.max(1, Math.round(video.videoWidth / scale));
            this.height = Math.max(1, Math.round(video.videoHeight / scale));
          }
        }
        
        this.isLoading = false
      }
    },
    getVideoFileSize () {
      // 从input中获取文件大小
      const input = this.$refs.videoInput;
      if (input && input.files && input.files[0]) {
        return input.files[0].size;
      }
      return 0;
    },
    updateTimeRange () {
      // 确保开始时间不大于结束时间
      if (this.startTime > this.endTime) {
        this.startTime = this.endTime - 1;
      }
      if (this.endTime < this.startTime) {
        this.endTime = this.startTime + 1;
      }
      // 确保不超过视频总时长
      if (this.videoInfo) {
        if (this.startTime < 0) this.startTime = 0;
        if (this.endTime > this.videoInfo.duration) this.endTime = this.videoInfo.duration;
      }
    },
    setFps (fps) {
      this.fps = fps;
      this.delay = Math.round(1000 / fps);
    },
    setPresetSize (width, height) {
      if (width === 'auto' && this.videoInfo) {
        // 保持原始比例，缩放至合适尺寸
        const maxW = this.isMobile ? this.maxProcessWidth : 800
        const maxH = this.isMobile ? this.maxProcessHeight : 600
        const scale = Math.min(maxW / this.videoInfo.width, maxH / this.videoInfo.height);
        this.width = Math.round(this.videoInfo.width * scale);
        this.height = Math.round(this.videoInfo.height * scale);
      } else {
        // 移动端自动限制尺寸
        if (this.isMobile) {
          const scaled = getScaledSize(width, height, this.maxProcessWidth, this.maxProcessHeight)
          this.width = scaled.width
          this.height = scaled.height
        } else {
          this.width = width;
          this.height = height;
        }
      }
    },
    updateSize () {
      // 确保尺寸在合理范围内
      const maxW = this.isMobile ? this.maxProcessWidth : 1920
      const maxH = this.isMobile ? this.maxProcessHeight : 1080
      if (this.width < 1) this.width = 1;
      if (this.height < 1) this.height = 1;
      if (this.width > maxW) this.width = maxW;
      if (this.height > maxH) this.height = maxH;
      this.calculateEstimatedSize();
    },
    calculateEstimatedSize () {
      if (!this.videoInfo) return;

      // 规范化时间与帧数计算
      const duration = Math.max(0, this.endTime - this.startTime);
      let frameCount = Math.min(Math.ceil(duration * this.fps), 500); // 最多500帧
      if (frameCount <= 0) frameCount = 1;

      // 估算每帧大小：基于像素数和质量（注意：quality 值越低越清晰，因此字节数应当越大）
      const pixelsPerFrame = Math.max(1, this.width) * Math.max(1, this.height);
      const bytesPerPixel = this.quality <= 10 ? 0.5 : (this.quality <= 20 ? 0.3 : 0.1); // 质量越好（数字小）占用越多

      // 颜色量化会减少色彩复杂度，从而减小每帧体积，使用一个粗略因子进行估计
      const quantizeFactor = this.useQuantization ? Math.max(0.25, (this.colorBits / 8)) : 1;
      const frameSize = pixelsPerFrame * bytesPerPixel * quantizeFactor;

      // 加上GIF文件头和调色板开销（粗略估算）
      const paletteOverhead = Math.round(Math.max(256, Math.pow(2, Math.min(8, this.colorBits))) * 2);
      const overhead = 2048 + paletteOverhead;

      this.estimatedSize = Math.round(frameCount * frameSize + overhead);
    },
    formatDuration (seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    },
    formatFileSizeDisplay (bytes) {
      return formatFileSize(bytes)
    },
    play () {
      // 只负责视频播放，不处理转换逻辑
      if (!this.videoUrl) {
        Toast('请先上传视频');
        return;
      }
      // 这里可以添加播放相关的逻辑，比如暂停转换等
    },
    async startConversion () {
      try {
        if (!this.videoInfo) {
          Toast('请先上传视频');
          return;
        }

        // 防止重复转换
        if (this.isConverting) {
          Toast('正在转换中，请等待完成');
          return;
        }

        // 验证参数
        if (this.startTime >= this.endTime) {
          Toast('开始时间不能大于或等于结束时间');
          return;
        }

        if (this.endTime > this.videoInfo.duration) {
          Toast('结束时间不能超过视频总时长');
          return;
        }

        if (this.width <= 0 || this.height <= 0) {
          Toast('宽度和高度必须大于0');
          return;
        }

        // 内存优化检查
        const conversionDuration = this.endTime - this.startTime;
        const estimatedFrames = Math.min(Math.ceil(conversionDuration * this.fps), 500);
        const estimatedMemoryMB = (this.width * this.height * estimatedFrames * 4) / (1024 * 1024); // RGBA

        if (estimatedMemoryMB > 200) {
          const result = confirm(`预计需要约 ${Math.round(estimatedMemoryMB)}MB 内存，可能会影响性能。是否继续？`);
          if (!result) return;
        }

        this.isConverting = true
        this.convertProgress = 0
        this.frameCount = 0
        this.gifImg = '' // 清空之前的预览

        let video = this.$refs.video
        const duration = this.endTime - this.startTime;
        const fps = this.fps;
        // cap total frames to avoid excessive memory usage for long/fast videos
        const maxFrames = this.isMobile ? 300 : 500
        this.totalFrames = Math.min(Math.ceil(duration * fps), maxFrames)

        // 设置视频开始时间
        video.currentTime = this.startTime;

      // 使用public目录下的worker脚本，路径更可靠
      const workerScriptUrl = './gif.worker.js';
      this.videoGif = new GIF({
        workers: this.isMobile ? 1 : 2,
        quality: +this.quality,
        workerScript: workerScriptUrl
      });

      // 监测渲染进度以防止卡住（stalled）在 70%
      this._gifLastProgressTime = Date.now();
      this._gifStallChecker = null;

      // 监听错误事件
      this.videoGif.on('error', (error) => {
        console.error('GIF生成错误:', error);
        this.handleConversionError('GIF生成失败，请重试');
      });

      // 监听进度事件（渲染阶段：70% -> 100%）
      this.videoGif.on('progress', (p) => {
        // 帧采集占70%，渲染占30%
        const percent = Math.round(70 + p * 30);
        this.convertProgress = percent;
        // 更新最后进度时间，用于检测卡住
        this._gifLastProgressTime = Date.now();
      });

      // 在调用 render 后启动一个检测器：如果渲染阶段超过 10s 无进展则重试或报错
      const startStallChecker = () => {
        if (this._gifStallChecker) clearInterval(this._gifStallChecker);
        this._gifLastProgressTime = Date.now();
        this._gifStallChecker = setInterval(() => {
          const now = Date.now();
          // 如果进度在渲染阶段（>=70）且超过 10s 无进展，则尝试重试 render 一次
          if (this.convertProgress >= 70 && (now - this._gifLastProgressTime) > 10000) {
            console.warn('GIF 渲染可能卡住，尝试重新触发 render');
            try {
              // 先尝试 abort 再重新 render
              if (this.videoGif && typeof this.videoGif.abort === 'function') {
                this.videoGif.abort();
              }
            } catch (e) {
              console.warn('abort 失败', e);
            }
            try {
              if (this.videoGif && typeof this.videoGif.render === 'function') {
                this.videoGif.render();
                this._gifLastProgressTime = Date.now();
              }
            } catch (e) {
              console.error('重新 render 失败', e);
              this.handleConversionError('GIF 渲染失败，请稍后重试');
            }
          }
        }, 2000);
      };

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext('2d');

      // 等待视频跳转到开始时间（seek 完成）
      await new Promise((resolve, reject) => {
        const onSeeked = () => {
          video.removeEventListener('seeked', onSeeked);
          resolve();
        };
        const timeout = setTimeout(() => {
          video.removeEventListener('seeked', onSeeked);
          if (!this.isConverting) {
            reject(new Error('转换已取消'));
          } else {
            // 如果 seek 很慢，也继续（降级处理）
            console.warn('视频 seek 超时，继续转换');
            resolve();
          }
        }, 3000);
        video.addEventListener('seeked', onSeeked);
        // 触发一次确保 seeked 会触发（有些浏览器会立即seek）
        try { video.currentTime = this.startTime; } catch (e) { /* ignore */ }
      });

      // 优先使用 requestVideoFrameCallback（如果可用），否则退回到 seek-by-frame 方法
      const frameInterval = 1 / fps;
      const totalFrames = this.totalFrames;

      const captureBySeek = async () => {
        try {
          let lastSeekTime = this.startTime - frameInterval;
          let seekPromises = [];

          for (let i = 0; i < totalFrames && this.isConverting; i++) {
            const t = Math.min(this.startTime + i * frameInterval, this.endTime);

            // 如果时间间隔足够大，进行seek，否则继续使用当前帧
            if (Math.abs(t - lastSeekTime) >= frameInterval * 0.5) {
              // 等待 seek 完成
              const seekPromise = new Promise((resolve) => {
                const onSeeked = () => {
                  video.removeEventListener('seeked', onSeeked);
                  // 减少延迟，提高速度
                  setTimeout(resolve, 20);
                };
                video.addEventListener('seeked', onSeeked);
                try {
                  video.currentTime = t;
                  lastSeekTime = t;
                } catch (e) {
                  // seek失败，跳过这一帧
                  setTimeout(resolve, 20);
                }
              });
              seekPromises.push(seekPromise);

              // 限制并发seek数量，避免浏览器过载
              if (seekPromises.length >= 3) {
                await Promise.race(seekPromises);
                seekPromises = seekPromises.filter(p => p !== seekPromises[0]);
              }
            }

            // 绘制并直接 addFrame
            canvas.width = +this.width;
            canvas.height = +this.height;
            canvas.delay = +this.delay;
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            // overlay
            this.drawOverlayOnCanvas(ctx, canvas);
            // quantize if enabled
            if (this.useQuantization) {
              this.applyQuantizationToCanvas(ctx, canvas, this.colorBits);
            }
            this.videoGif.addFrame(canvas, { copy: true, delay: canvas.delay });

            this.frameCount++;
            if (totalFrames > 0) {
              this.convertProgress = Math.round((this.frameCount / totalFrames) * 70);
            }

            // 每处理几帧让出主线程，移动端更频繁
            if (i % this.batchSize === 0) {
              await yieldToMain()
            }
          }

          // 等待所有pending的seek完成
          await Promise.all(seekPromises);

          if (this.isConverting && this.videoGif) {
            this.videoGif.render();
          }
        } catch (err) {
          console.error('seek 捕获失败:', err);
          this.handleConversionError('帧捕获失败，请重试');
        }
      };

      const captureByRequestVideoFrame = async () => {
        try {
          // 尝试播放视频（某些浏览器要求 play 触发 rVFC）
          try { await video.play(); } catch (e) { /* ignore play errors */ }

          let lastCaptureTime = this.startTime - frameInterval - 0.001;
          let captured = 0;

          const onFrame = (now, metadata) => {
            if (!this.isConverting) {
              try { video.pause(); } catch (e) {}
              if (this.videoGif) this.videoGif.render();
              return;
            }
            const mediaTime = typeof metadata.mediaTime === 'number' ? metadata.mediaTime : video.currentTime;
            if (mediaTime < this.startTime) {
              video.requestVideoFrameCallback(onFrame);
              return;
            }
            if (mediaTime > this.endTime || captured >= totalFrames) {
              try { video.pause(); } catch (e) {}
              if (this.videoGif) this.videoGif.render();
              return;
            }
            if ((mediaTime - lastCaptureTime) >= (frameInterval - 0.001)) {
              // capture
              canvas.width = +this.width;
              canvas.height = +this.height;
              canvas.delay = +this.delay;
              ctx.fillStyle = '#ffffff';
              ctx.fillRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              // overlay
              this.drawOverlayOnCanvas(ctx, canvas);
              // quantize if enabled
              if (this.useQuantization) {
                this.applyQuantizationToCanvas(ctx, canvas, this.colorBits);
              }
              this.videoGif.addFrame(canvas, { copy: true, delay: canvas.delay });
              captured++;
              this.frameCount = captured;
              if (totalFrames > 0) {
                this.convertProgress = Math.round((this.frameCount / totalFrames) * 70);
              }
              lastCaptureTime = mediaTime;
            }
            video.requestVideoFrameCallback(onFrame);
          };

          video.requestVideoFrameCallback(onFrame);
        } catch (err) {
          console.warn('requestVideoFrameCallback 捕获失败，回退到 seek 方法', err);
          await captureBySeek();
        }
      };

      // 优先使用 seek-by-frame 方法（不需要播放视频），rVFC仅在高帧率时使用
      if (video.requestVideoFrameCallback && this.fps >= 24) {
        // 只有在高帧率(>=24fps)时才使用rVFC，因为它更精确但需要播放视频
        await captureByRequestVideoFrame();
      } else {
        await captureBySeek();
      }
      // 在开始渲染之前启动卡住检测
      startStallChecker();

        this.videoGif.on('finished', async blob => {
          this.convertProgress = 100
          // 使用data URL而不是blob URL，这样在小程序webview中可以长按保存
          // 注意：data URL不需要revoke，所以不需要清理
          this.gifImg = await blobToBase64(blob)
          this.isConverting = false
          // 清理卡住检测器
          if (this._gifStallChecker) {
            clearInterval(this._gifStallChecker);
            this._gifStallChecker = null;
          }
          
          // 释放 canvas 内存
          canvas.width = 1
          canvas.height = 1
        }) 
      } catch (error) {
        console.error('转换过程中发生错误:', error);
        this.handleConversionError(error.message || '转换失败，请重试');
      }
    },
    handleConversionError (message) {
      this.isConverting = false;
      this.convertProgress = 0;
      this.frameCount = 0;
      this.totalFrames = 0;
      clearInterval(this.videoTimer);
      Toast.fail(message);
    },
    cancelConversion () {
      if (this.videoTimer) {
        clearInterval(this.videoTimer);
      }
      if (this.videoGif) {
        // 尝试停止GIF渲染
        try {
          this.videoGif.abort();
        } catch (e) {
          // 忽略错误
        }
      }
      this.isConverting = false;
      this.convertProgress = 0;
      this.frameCount = 0;
      this.totalFrames = 0;
      Toast('转换已取消');
    },
    pause () {
      // 仅处理播放暂停，不自动取消正在进行的转换。
      // 之前在播放结束（ended）会触发 pause，导致转换被意外停止。
      // 如果没有正在转换，则清理旧的定时器。
      if (!this.isConverting) {
        clearInterval(this.videoTimer)
      }
      // 不对正在进行的转换做任何取消动作——用户应使用"取消"按钮停止转换。
    },
    uploaderVideo (e) {
      const videoFile = e.target.files[0];
      if (videoFile) {
        // 文件大小检查
        const check = checkFileSize(videoFile, this.maxVideoSize)
        if (!check.valid) {
          Toast.fail(check.message)
          return
        }
        
        this.processVideoFile(videoFile);
      }
    },
    loadImg ({gif, canvas, ctx, img } = config) {
      return new Promise(resolve => {
        const imgImage = new Image();
        imgImage.src = img;
        ctx.fillRect(0, 0, canvas.width, canvas.height);//铺底色
        imgImage.onload = (e) => {
          ctx.drawImage(imgImage, 0, 0, canvas.width, canvas.height);
          gif.addFrame(canvas, {copy: true, delay: canvas.delay});
          resolve()
        }
      })
    },
    applyQuantizationToCanvas (ctx, canvas, bits) {
      try {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        const shift = 8 - bits;
        for (let i = 0; i < data.length; i += 4) {
          data[i] = (data[i] >> shift) << shift;     // R
          data[i+1] = (data[i+1] >> shift) << shift; // G
          data[i+2] = (data[i+2] >> shift) << shift; // B
          // keep alpha as is
        }
        ctx.putImageData(imageData, 0, 0);
      } catch (e) {
        // 某些跨域视频可能会阻止 getImageData，忽略并继续
        console.warn('applyQuantizationToCanvas failed', e);
      }
    },
    drawOverlayOnCanvas (ctx, canvas) {
      if (!this.overlayText) return;
      ctx.save();
      ctx.globalAlpha = this.overlayOpacity;
      ctx.fillStyle = this.overlayColor || '#ffffff';
      const fontSize = Math.max(8, Number(this.overlayFontSize) || 16);
      ctx.font = `${fontSize}px sans-serif`;
      ctx.textBaseline = 'bottom';
      let x = canvas.width - 10;
      let y = canvas.height - 10;
      ctx.textAlign = 'right';
      if (this.overlayPosition === 'bottom-left') { x = 10; ctx.textAlign = 'left'; }
      if (this.overlayPosition === 'top-right') { y = fontSize + 10; ctx.textBaseline = 'top'; }
      if (this.overlayPosition === 'top-left') { x = 10; y = fontSize + 10; ctx.textAlign = 'left'; ctx.textBaseline = 'top'; }
      if (this.overlayPosition === 'center') { x = canvas.width / 2; y = canvas.height / 2; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; }
      // draw shadow for readability
      ctx.fillStyle = 'rgba(0,0,0,0.45)';
      ctx.fillText(this.overlayText, x + 1, y + 1);
      ctx.fillStyle = this.overlayColor || '#ffffff';
      ctx.fillText(this.overlayText, x, y);
      ctx.restore();
    },
    async toDownloadVideo () {
      if (!this.videoUrl) {
        return Toast('请先选择视频')
      }
      if (!this.gifImg) {
        return Toast('请播放视频生成预览图')
      }
      await downloadFile(this.gifImg, `video_gif_${Date.now()}.gif`)
    },
    handleDrop (e) {
      this.dragLeave();
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        if (file.type.startsWith('video/')) {
          // 文件大小检查
          const check = checkFileSize(file, this.maxVideoSize)
          if (!check.valid) {
            Toast.fail(check.message)
            return
          }
          this.processVideoFile(file);
        } else {
          Toast('请上传视频文件');
        }
      }
    },
    dragEnter () {
      this.isDragging = true;
    },
    dragLeave () {
      this.isDragging = false;
    },
    processVideoFile (file) {
      // 显示加载提示
      this.isLoading = true
      this.loadingText = '正在加载视频...'
      this.loadingSubtext = `文件大小: ${formatFileSize(file.size)}`
      this.loadingProgress = -1
      
      let url = URL.createObjectURL(file);
      this.videoUrl = url;
      // 手动设置input的files，这样可以获取文件大小
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      this.$refs.videoInput.files = dataTransfer.files;
    }
  }
}
</script>

<style>
.video-wrapper {
  padding: 8px 0;
}

.section-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 16px;
  color: #1f2933;
}

.upload-area {
  border: 2px dashed #9fb3ff;
  border-radius: 16px;
  padding: 32px 16px;
  background: #f8f9ff;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  margin-bottom: 16px;
}

.upload-area:hover {
  border-color: #667eea;
  background: #f0f2ff;
}

.upload-area.dragging {
  border-color: #4f46e5;
  background: #e0e7ff;
  transform: scale(1.02);
}

.upload-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.upload-icon {
  font-size: 40px;
  margin-bottom: 4px;
}

.upload-text {
  font-size: 16px;
  color: #4f46e5;
  font-weight: 600;
}

.upload-hint {
  font-size: 12px;
  color: #9ca3af;
}

.size-hint {
  font-size: 11px;
  color: #b4b4b4;
  margin-top: 4px;
}

.hidden-video-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  overflow: hidden;
}

.preview-container {
  margin-bottom: 16px;
}

.preview-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #374151;
}

.video {
  width: 100%;
  max-height: 260px;
  border-radius: 12px;
  background: black;
}

.controls {
  margin-top: 8px;
}

.control-group {
  margin-bottom: 16px;
}

.control-group.inline {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.control-label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.inline-label {
  margin-left: 8px;
}

.input {
  width: 80px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 4px 6px;
  font-size: 13px;
}

.size-input-small {
  width: 60px;
  text-align: center;
}

/* 水印文字输入：占满可用宽度，保持当前风格但更易编辑长文本 */
.overlay-text-input {
  width: 100%;
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  font-size: 14px;
  box-sizing: border-box;
  margin-bottom: 8px;
}

.range {
  width: 100%;
  margin-top: 8px;
}

.value-display {
  margin-left: 4px;
  color: #4f46e5;
  font-weight: 600;
}

.actions {
  margin-top: 8px;
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.convert-btn {
  flex: 2;
}

.download-btn {
  flex: 1;
}

.helper-text {
  margin-top: 6px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}

.preview-img {
  display: block;
  margin: 16px auto 0;
  max-width: 100%;
  max-height: 260px;
  border-radius: 12px;
  box-shadow: 0 6px 12px rgba(15, 23, 42, 0.15);
}

.progress-container {
  margin: 16px 0;
  padding: 12px;
  background: #f8f9ff;
  border-radius: 8px;
}

.progress-label {
  font-size: 13px;
  color: #4f46e5;
  font-weight: 500;
  margin-bottom: 8px;
  text-align: center;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-actions {
  margin-top: 12px;
  text-align: center;
}

.video-info {
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.info-item {
  font-size: 12px;
  color: #6b7280;
  background: #f3f4f6;
  padding: 4px 8px;
  border-radius: 4px;
}

.time-range-container {
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.time-input-group {
  display: flex;
  align-items: center;
  gap: 4px;
}

.time-label {
  font-size: 12px;
  color: #6b7280;
  min-width: 30px;
}

.time-input {
  width: 70px;
}

.time-unit {
  font-size: 12px;
  color: #9ca3af;
}

.time-range-slider {
  position: relative;
  width: 100%;
  height: 20px;
}

.time-slider {
  position: absolute;
  width: 100%;
  height: 4px;
  background: transparent;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
}

.time-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #667eea;
  cursor: pointer;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.time-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #667eea;
  cursor: pointer;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.fps-presets {
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.preset-btn {
  padding: 4px 8px;
  font-size: 11px;
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.preset-btn:hover {
  background: #f3f4f6;
  border-color: #667eea;
}

.preset-btn.active {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.size-input-group {
  margin-bottom: 8px;
}

.size-inputs {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.size-label {
  font-size: 12px;
  color: #6b7280;
  min-width: 35px;
}

.size-input {
  width: 70px;
}

.size-separator {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

.size-unit {
  font-size: 12px;
  color: #9ca3af;
  margin-left: 4px;
}

.size-presets {
  display: flex;
  gap: 6px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.size-presets .preset-btn {
  padding: 4px 6px;
  font-size: 10px;
  min-width: 50px;
}

.estimated-size {
  font-size: 16px;
  font-weight: 600;
  color: #4f46e5;
  margin-top: 4px;
}

.size-warning {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  font-size: 12px;
  color: #f59e0b;
}

.warning-icon {
  font-size: 14px;
}
</style>
